// Wheel of Fate (docs/04 §17.3): a carnival wheel with three rings and a swinging timing meter.
// Pressing STOP inside the band earns a second spin (keep the better) — the view reports only
// whether the stop was inside the band; the sim picks the segment.
import { Container, Graphics, Sprite, type Ticker } from 'pixi.js';
import { PALETTE, type PaletteKey } from '../../art/palette';
import { getAtlas, textTexture } from '../../art/rasterize';
import { audio } from '../../audio/audio';
import { formatNumber } from '../../i18n/format';
import { t } from '../../i18n/t';
import { coinPar, nightSpins, wheelSegments } from '../../sim/bonus/wheel';
import { computeModifiers } from '../../sim/modifiers';
import type { WheelBonus } from '../../sim/types';
import { dispatch, getGame } from '../../state/store';
import { LOGICAL_W } from '../app';
import type { Popups } from '../fx/Popups';
import { PixelButton } from '../machine/PixelButton';
import { clock } from '../timeline/clock';

const W = 300;
const H = 200;
const R = 72;
const METER_W = 110;
/** Base timing band as a share of the meter (Wheel Grease and Lucky Spokes widen it). */
const BAND = 0.18;

const wheel = (): WheelBonus | null => {
  const b = getGame().run?.bonus;
  return b?.mode === 'wheel' ? b : null;
};

function segStyle(seg: string, i: number): { fill: PaletteKey; text: PaletteKey } {
  if (seg === 'step') return { fill: 'lavender', text: 'ink' };
  if (nightSpins(seg) !== null) return { fill: 'navy', text: 'sky' };
  if (seg === 'charm') return { fill: 'mint', text: 'ink' };
  if (seg === 'shard') return { fill: 'pinkLilac', text: 'ink' };
  if (seg === 'legendary') return { fill: 'amber', text: 'ink' };
  if (seg === 'plug') return { fill: 'ink', text: 'lavender' };
  return i % 2 ? { fill: 'red', text: 'white' } : { fill: 'paper', text: 'ink' };
}

/** Special segments show an icon; coin segments show their par value. */
const SEG_ICON: Record<string, [string, number]> = {
  charm: ['charm_penny', 0.5],
  shard: ['item_shard', 0.5],
  legendary: ['sym_crown', 0.4],
  plug: ['item_plug_5', 0.5],
};

function segLabel(seg: string): string {
  if (seg === 'step') return t('ui.wheel.seg.step');
  const night = nightSpins(seg);
  if (night !== null) return t('ui.wheel.seg.night', { n: night });
  const coins = coinPar(seg);
  if (coins !== null) return String(coins);
  return t(`ui.wheel.seg.${seg}`);
}

export class WheelView extends Container {
  private panel = new Container();
  private disc = new Container();
  private meter = new Graphics();
  private marker = new Graphics();
  private ringLabel = new Sprite();
  private legend = new Container();
  private stopBtn = new PixelButton(80, 15, t('ui.wheel.stop'));
  private phase = 0;
  private spinning = true;
  private waiting = false;
  private band = BAND;
  private ring = -1;
  private seen = 0;

  constructor(private popups: Popups) {
    super();
    this.visible = false;
    this.addChild(this.panel);
    const bg = new Graphics();
    bg.rect(-2, -2, W + 4, H + 4).fill(PALETTE.ink);
    bg.rect(0, 0, W, H).fill(PALETTE.wine);
    bg.rect(0, 0, W, 14).fill(PALETTE.gold);
    const title = new Sprite(textTexture(t('ui.wheel.title'), 'ink', 'brass'));
    title.position.set(Math.round((W - title.width) / 2), 3);
    const hint = new Sprite(textTexture(t('ui.wheel.hint'), 'paper', 'ink'));
    hint.scale.set(1);
    hint.position.set(6, H - 12);
    this.disc.position.set(90, 104);
    const a = getAtlas();
    const hub = new Sprite(a.tex('wheel_hub'));
    hub.anchor.set(0.5);
    hub.position.set(90, 104);
    const pointer = new Sprite(a.tex('wheel_pointer'));
    pointer.anchor.set(0.5, 0);
    pointer.position.set(90, 104 - R - 10);
    this.meter.position.set(180, 64);
    this.marker.position.set(180, 64);
    this.ringLabel.position.set(180, 40);
    this.legend.position.set(180, 136);
    this.stopBtn.position.set(195, 86);
    this.stopBtn.onClick = () => this.stop();
    this.panel.addChild(bg, title, this.disc, hub, pointer, this.meter, this.marker, this.ringLabel, this.stopBtn, this.legend, hint);
    this.panel.eventMode = 'static';
  }

  bindTicker(ticker: Ticker): void {
    ticker.add((tk) => {
      if (!this.visible) return;
      if (this.spinning && !this.waiting) this.disc.rotation += 0.004 * tk.deltaMS;
      this.phase += tk.deltaMS / 550;
      const x = ((Math.sin(this.phase) + 1) / 2) * METER_W;
      this.marker.clear().rect(Math.round(x) - 1, -3, 3, 16).fill(PALETTE.white).rect(Math.round(x) - 1, -3, 3, 16).stroke({ color: PALETTE.ink, width: 1 });
    });
  }

  private markerInBand(): boolean {
    const x = (Math.sin(this.phase) + 1) / 2;
    return Math.abs(x - 0.5) <= this.band / 2;
  }

  stop(): void {
    const b = wheel();
    if (!b || b.done || this.waiting) return;
    this.waiting = true;
    this.stopBtn.set(false);
    const hit = this.markerInBand();
    if (hit) void this.popups.float(t('ui.wheel.hit'), this.panel.x + 235, this.panel.y + 80, 'mint', 1, 700);
    audio.play('clack');
    dispatch({ t: 'BONUS_INPUT', input: { kind: 'wheel_stop', hit } });
  }

  private drawRing(ring: number): void {
    this.ring = ring;
    this.disc.removeChildren().forEach((c) => c.destroy());
    const segs = wheelSegments(ring);
    const n = segs.length;
    const step = (Math.PI * 2) / n;
    const g = new Graphics();
    g.circle(0, 0, R + 3).fill(PALETTE.ink);
    g.circle(0, 0, R + 2).fill(PALETTE.brass);
    segs.forEach((seg, i) => {
      const a0 = -Math.PI / 2 + i * step - step / 2;
      const st = segStyle(seg, i);
      g.moveTo(0, 0).arc(0, 0, R, a0, a0 + step).lineTo(0, 0).fill(PALETTE[st.fill]);
      g.moveTo(0, 0).lineTo(Math.cos(a0) * R, Math.sin(a0) * R).stroke({ color: PALETTE.ink, width: 1 });
    });
    this.disc.addChild(g);
    const a0 = getAtlas();
    segs.forEach((seg, i) => {
      const a = -Math.PI / 2 + i * step;
      const st = segStyle(seg, i);
      const icon = nightSpins(seg) !== null ? (['sym_moon', 0.45] as [string, number]) : SEG_ICON[seg];
      const text = seg === 'step' ? '▲▲' : String(coinPar(seg) ?? '');
      const label = icon ? new Sprite(a0.tex(icon[0])) : new Sprite(textTexture(text, st.text, null));
      if (icon) label.scale.set(icon[1]);
      label.anchor.set(0.5);
      label.position.set(Math.round(Math.cos(a) * R * 0.66), Math.round(Math.sin(a) * R * 0.66));
      label.rotation = a + Math.PI / 2;
      this.disc.addChild(label);
    });
    this.ringLabel.texture = textTexture(t(`ui.wheel.ring.${ring}`), 'gold', 'ink');
    // Legend for this ring's special segments.
    this.legend.removeChildren().forEach((c) => c.destroy());
    [...new Set(segs.filter((s) => coinPar(s) === null))].forEach((seg, k) => {
      const icon = nightSpins(seg) !== null ? (['sym_moon', 0.45] as [string, number]) : SEG_ICON[seg];
      const sp = icon ? new Sprite(a0.tex(icon[0])) : new Sprite(textTexture('▲▲', 'lavender', 'ink'));
      if (icon) sp.scale.set(Math.min(icon[1], 0.5));
      sp.anchor.set(0.5);
      sp.position.set(8, k * 14 + 6);
      const tx = new Sprite(textTexture(segLabel(seg), 'paper', 'ink'));
      tx.position.set(20, k * 14 + 2);
      this.legend.addChild(sp, tx);
    });
  }

  private drawMeter(): void {
    const m = this.meter.clear();
    m.rect(-1, -1, METER_W + 2, 12).fill(PALETTE.ink);
    m.rect(0, 0, METER_W, 10).fill(PALETTE.night);
    const bw = Math.round(METER_W * this.band);
    m.rect(Math.round((METER_W - bw) / 2), 0, bw, 10).fill(PALETTE.clover);
  }

  open(): void {
    const b = wheel();
    if (!b) return;
    this.band = Math.min(0.9, BAND * computeModifiers(getGame().meta, getGame().run).wheelBand);
    this.seen = b.stops.length;
    this.waiting = false;
    this.spinning = true;
    this.drawRing(b.ring);
    this.drawMeter();
    this.stopBtn.set(true);
    this.panel.position.set(Math.round(LOGICAL_W / 2 - W / 2 - 20), 360);
    this.visible = true;
    void clock.tween(this.panel, 350, { y: 74, ease: 'back.out(1.4)' });
  }

  /** The sim stopped the wheel: decelerate onto the segment, then step in or wait for the end. */
  async update(): Promise<void> {
    const b = wheel();
    if (!b || b.stops.length <= this.seen) return;
    this.seen = b.stops.length;
    const ring = this.ring;
    const segs = wheelSegments(ring);
    const idx = b.stops[b.stops.length - 1]!;
    const step = (Math.PI * 2) / segs.length;
    const current = this.disc.rotation;
    const target = -idx * step;
    let to = target;
    while (to < current + Math.PI * 4) to += Math.PI * 2;
    let lastTick = Math.floor(current / step);
    await clock.tween(this.disc, 1700, {
      rotation: to,
      ease: 'power3.out',
      onUpdate: () => {
        const k = Math.floor(this.disc.rotation / step);
        if (k !== lastTick) {
          lastTick = k;
          audio.play('wheelTick', { pitch: 4 });
        }
      },
    });
    this.disc.rotation = to % (Math.PI * 2);
    this.spinning = false;
    const prize = b.prizes[b.prizes.length - 1] ?? '';
    const mid = { x: this.panel.x + 90, y: this.panel.y + 104 };
    audio.play(prize === 'step' ? 'swell' : 'register');
    const coins = coinPar(prize);
    const name = coins !== null ? t('ui.wheel.seg.coins', { n: coins }) : segLabel(prize);
    await this.popups.banner(name, mid.x + 60, mid.y - 70, 'gold', 2, 500);
    if (!b.done && b.ring !== ring) {
      // STEP IN: the wheel shrinks into its next ring.
      await clock.tween(this.disc.scale, 250, { x: 0.2, y: 0.2 });
      this.drawRing(b.ring);
      this.disc.scale.set(0.2);
      await clock.tween(this.disc.scale, 300, { x: 1, y: 1, ease: 'back.out(2)' });
      this.spinning = true;
      this.waiting = false;
      this.stopBtn.set(true);
    }
  }

  async close(payout: string, _voided: boolean): Promise<void> {
    const mid = { x: this.panel.x + W / 2, y: this.panel.y + H / 2 };
    if (payout !== '0') {
      audio.play('scratchWin');
      await this.popups.banner(t('ui.scratch.win', { coins: formatNumber(payout) }), mid.x, mid.y, 'gold', 2, 600);
    }
    await clock.tween(this.panel, 300, { y: 380, ease: 'back.in(1.4)' });
    this.visible = false;
  }
}
